<template>
  <div class="departments-page">
    <!-- ===================== Hero Header ===================== -->
    <div class="dep-hero">
      <div class="dep-hero__blob dep-hero__blob--one" />
      <div class="dep-hero__blob dep-hero__blob--two" />

      <div class="dep-hero__content">
        <div class="dep-hero__icon">
          <feather-icon
            icon="GridIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="dep-hero__title">
            Departments
          </h2>
          <p class="dep-hero__subtitle">
            Organise your departments and explore the product categories that belong to each one.
          </p>
        </div>
      </div>

      <button
        type="button"
        class="dep-add-btn"
        @click="goToAdd"
      >
        <span class="dep-add-btn__icon">
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">{{ addType }}</span>
      </button>
    </div>

    <!-- ===================== Stats ===================== -->
    <b-row class="match-height mb-1">
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--indigo"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalDepartments }}
              </h2>
              <span class="text-white-75">Departments</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="GridIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="GridIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--blue"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalProducts }}
              </h2>
              <span class="text-white-75">Products</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="PackageIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="PackageIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--cyan"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.withProducts }}
              </h2>
              <span class="text-white-75">With Products</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="CheckCircleIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--teal"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.empty }}
              </h2>
              <span class="text-white-75">Without Products</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="InboxIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="InboxIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="dep-toolbar-card mb-1"
    >
      <div class="dep-toolbar">
        <div class="dep-toolbar__title">
          <b-avatar
            rounded
            variant="light-primary"
            size="40"
            class="mr-1"
          >
            <feather-icon
              icon="LayersIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              All Departments
            </h4>
            <small class="text-muted">Titles, descriptions and linked products</small>
          </div>
        </div>

        <div class="dep-toolbar__actions">
          <b-input-group class="dep-search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search departments..."
              autocomplete="off"
              @keyup.enter="searchNow"
            />
            <b-input-group-append
              v-if="searchQuery"
              is-text
              class="dep-search__clear"
              @click="clearSearch"
            >
              <feather-icon icon="XIcon" />
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="dep-skel-card">
          <div class="dep-skel-accent" />
          <div class="dep-skel-head">
            <div class="dep-skel-avatar" />
            <div class="dep-skel-lines">
              <div class="dep-skel-line dep-skel-line--short" />
              <div class="dep-skel-line dep-skel-line--long" />
            </div>
          </div>
          <div class="dep-skel-body">
            <div class="dep-skel-chip" />
            <div class="dep-skel-chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty state ===================== -->
    <b-card
      v-else-if="!filteredRows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No departments found</h4>
      <p class="text-muted mb-1">
        {{ searchQuery ? 'Try adjusting your search.' : 'Start by adding your first department.' }}
      </p>
      <button
        v-if="!searchQuery"
        type="button"
        class="dep-add-btn dep-add-btn--solid mx-auto"
        @click="goToAdd"
      >
        <span class="dep-add-btn__icon">
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">{{ addType }}</span>
      </button>
    </b-card>

    <!-- ===================== Department cards ===================== -->
    <b-row
      v-else
      class="departments-grid"
    >
      <b-col
        v-for="row in filteredRows"
        :key="row.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="dep-card">
          <div
            class="dep-accent"
            :style="{ background: getGradient(row) }"
          />

          <!-- Header -->
          <div class="dep-card-head">
            <div
              class="dep-avatar"
              :style="{ background: getGradient(row) }"
            >
              <feather-icon
                :icon="getIcon(row)"
                size="22"
              />
            </div>

            <div class="dep-head-text">
              <h4
                class="dep-name"
                :title="row.title_en || row.title"
              >
                {{ row.title_en || row.title }}
              </h4>
              <span
                v-if="row.title_ar && row.title_ar !== (row.title_en || row.title)"
                class="dep-name-ar"
                dir="rtl"
              >{{ row.title_ar }}</span>
            </div>

            <span class="dep-id">#{{ row.id }}</span>
          </div>

          <!-- Body -->
          <div class="dep-card-body">
            <div
              v-if="row.description_en || row.description"
              class="dep-desc"
              :title="row.description_en || row.description"
            >
              {{ row.description_en || row.description }}
            </div>
            <div
              v-if="row.description_ar && row.description_ar !== (row.description_en || row.description)"
              class="dep-desc dep-desc--ar"
              dir="rtl"
              :title="row.description_ar"
            >
              {{ row.description_ar }}
            </div>

            <div class="dep-section">
              <div class="section-label">
                <feather-icon
                  icon="PackageIcon"
                  size="14"
                  class="mr-25"
                />
                <span>Products</span>
                <b-badge
                  pill
                  :variant="productsOf(row).length ? 'light-primary' : 'light-secondary'"
                  class="ml-50"
                >
                  {{ productsOf(row).length }}
                </b-badge>
              </div>

              <div
                v-if="productsOf(row).length"
                class="product-chips"
              >
                <span
                  v-for="product in previewProducts(row)"
                  :key="product.id"
                  class="product-chip"
                  :title="product.name"
                >
                  {{ product.name }}
                </span>

                <button
                  v-if="productsOf(row).length > previewLimit"
                  type="button"
                  class="product-chip product-chip--more"
                  @click="openProducts(row)"
                >
                  +{{ productsOf(row).length - previewLimit }} more
                </button>
              </div>
              <span
                v-else
                class="empty-hint"
              >No products linked yet</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="dep-card-footer">
            <button
              type="button"
              class="footer-btn footer-btn--view"
              :disabled="!productsOf(row).length"
              @click="openProducts(row)"
            >
              <feather-icon
                icon="ListIcon"
                size="14"
                class="mr-25"
              />
              View products
            </button>

            <a
              :href="getEditLink(row)"
              target="_blank"
              rel="noopener"
              class="footer-btn footer-btn--edit"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              Edit
            </a>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Pagination ===================== -->
    <div
      v-if="!dataLoad && filteredRows.length"
      class="mt-1"
    >
      <Pagination
        :page="page"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="getAllData"
      />
    </div>

    <!-- ===================== Products modal ===================== -->
    <b-modal
      id="dep-products-modal"
      v-model="productsModal"
      centered
      scrollable
      hide-footer
      size="lg"
      modal-class="dep-modal"
    >
      <template #modal-title>
        <div class="dep-modal__title">
          <span
            class="dep-modal__icon"
            :style="{ background: activeDepartment ? getGradient(activeDepartment) : '' }"
          >
            <feather-icon
              :icon="activeDepartment ? getIcon(activeDepartment) : 'PackageIcon'"
              size="16"
            />
          </span>
          <div>
            <div class="dep-modal__name">
              {{ activeDepartment ? (activeDepartment.title_en || activeDepartment.title) : '' }}
            </div>
            <small class="text-muted">{{ activeProducts.length }} product categories</small>
          </div>
        </div>
      </template>

      <div class="dep-modal-search">
        <b-input-group>
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="modalSearch"
            placeholder="Filter products..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="modalSearch"
            is-text
            class="dep-search__clear"
            @click="modalSearch = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>
      </div>

      <div
        v-if="activeProducts.length"
        class="dep-modal-list"
      >
        <div
          v-for="(product, index) in activeProducts"
          :key="product.id"
          class="dep-modal-item"
        >
          <span class="dep-modal-item__num">{{ index + 1 }}</span>
          <span class="dep-modal-item__text">{{ product.name }}</span>
        </div>
      </div>
      <div
        v-else
        class="text-center text-muted py-3"
      >
        No products match your filter.
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import {
  BRow, BCol, BCard, BCardBody, BAvatar, BBadge, BModal,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'

const DEPARTMENT_ICON_RULES = [
  { keys: ['admin'], icon: 'BriefcaseIcon' },
  { keys: ['human', 'hr', 'resource'], icon: 'UsersIcon' },
  { keys: ['finance', 'account'], icon: 'DollarSignIcon' },
  { keys: ['marketing', 'sales'], icon: 'TrendingUpIcon' },
  { keys: ['sfda', 'food', 'drug'], icon: 'ActivityIcon' },
  { keys: ['goeic', 'export', 'import'], icon: 'GlobeIcon' },
  { keys: ['qatar', 'coc'], icon: 'MapPinIcon' },
  { keys: ['conformity', 'quality'], icon: 'CheckCircleIcon' },
  { keys: ['it', 'tech', 'system'], icon: 'CpuIcon' },
  { keys: ['halal'], icon: 'AwardIcon' },
  { keys: ['lab', 'chem'], icon: 'ThermometerIcon' },
  { keys: ['legal', 'audit'], icon: 'ShieldIcon' },
]

const DEPARTMENT_ICON_FALLBACK = ['GridIcon', 'LayersIcon', 'BoxIcon', 'FolderIcon']

// Cool, harmonious palette: indigo -> blue -> cyan -> teal
const DEPARTMENT_GRADIENTS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #5f6ef5, #8f9bf8)',
  'linear-gradient(135deg, #4f8ef7, #86b6fb)',
  'linear-gradient(135deg, #2fa8e0, #6fcdef)',
  'linear-gradient(135deg, #29b6ad, #6fd6cd)',
  'linear-gradient(135deg, #8a7bf0, #b3a9f7)',
]

export default {
  components: {
    Pagination,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BAvatar,
    BBadge,
    BModal,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      apiUrl: 'departments',
      addType: 'Add Department',
      addComponentName: 'add-departments',
      editComponent: 'edit-departments',
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchQuery: '',
      previewLimit: 6,
      productsModal: false,
      modalSearch: '',
      activeDepartment: null,
      stats: {
        totalDepartments: 0,
        totalProducts: 0,
        withProducts: 0,
        empty: 0,
      },
    }
  },
  computed: {
    filteredRows() {
      const term = this.searchQuery.trim().toLowerCase()
      if (!term) return this.rows

      return this.rows.filter(row => [
        row.title,
        row.title_en,
        row.title_ar,
        row.description,
        row.description_en,
        row.description_ar,
      ].some(field => (field || '').toLowerCase().includes(term)))
    },
    activeProducts() {
      const products = this.productsOf(this.activeDepartment)
      const term = this.modalSearch.trim().toLowerCase()
      if (!term) return products

      return products.filter(product => (product.name || '').toLowerCase().includes(term))
    },
  },
  mounted() {
    this.getAllData(this.page)
  },
  methods: {
    async getAllData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const params = { skip: (page - 1) * this.perPage }
        const response = await axios.get(this.apiUrl, { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)?.data
          this.rows = this.extractRows(responseData)
          this.totalRows = responseData?.count || this.rows.length
          this.computeStats()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load departments',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.dataLoad = false
      }
    },

    extractRows(data) {
      if (Array.isArray(data)) return data
      if (!data) return []

      const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]))
      return arrayKey ? data[arrayKey] : []
    },

    computeStats() {
      const totalProducts = this.rows.reduce((sum, row) => sum + this.productsOf(row).length, 0)
      const withProducts = this.rows.filter(row => this.productsOf(row).length).length

      this.stats = {
        totalDepartments: this.totalRows || this.rows.length,
        totalProducts,
        withProducts,
        empty: this.rows.length - withProducts,
      }
    },

    productsOf(row) {
      return (row && row.products) || []
    },

    previewProducts(row) {
      return this.productsOf(row).slice(0, this.previewLimit)
    },

    openProducts(row) {
      if (!this.productsOf(row).length) return
      this.activeDepartment = row
      this.modalSearch = ''
      this.productsModal = true
    },

    searchNow() {
      this.getAllData(1)
    },

    clearSearch() {
      this.searchQuery = ''
    },

    goToAdd() {
      this.$router.push({ name: this.addComponentName })
    },

    getEditLink(row) {
      return this.$router.resolve({
        name: this.editComponent,
        params: { id: row.id, name: row.title_en || row.title },
      }).href
    },

    getIcon(row) {
      const name = `${row.title_en || row.title || ''}`.toLowerCase()
      const rule = DEPARTMENT_ICON_RULES.find(({ keys }) => keys.some(key => name.includes(key)))

      if (rule) return rule.icon

      return DEPARTMENT_ICON_FALLBACK[row.id % DEPARTMENT_ICON_FALLBACK.length]
    },

    getGradient(row) {
      return DEPARTMENT_GRADIENTS[Math.abs(Number(row.id) || 0) % DEPARTMENT_GRADIENTS.length]
    },
  },
}
</script>

<style lang="scss" scoped src="./Departments.scss"></style>
